import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Heart, Shield, Linkedin, Mail } from 'lucide-react';
import Button from '../../components/Button/Button';
import Card from '../../components/Card/Card';
import './About.css';

const About: React.FC = () => {

  const team = [
    {
      id: 'ritesh-nautiyal',
      name: 'Ritesh Nautiyal',
      role: 'Founder and CEO',
      bio: 'Over two decades of experience across four continents in tennis and racquet sports, specializing in program development, strategic leadership, and high-performance management.',
      image: '/ritesh.png',
      linkedin: 'https://www.linkedin.com/in/ritesh-nautiyal-240a31147',
      email: 'nautiyalritesh6@gmail.com',
    },
    {
      id: 'shashank-nautiyal',
      name: 'Shashank Nautiyal',
      role: 'Partner',
      bio: 'Seasoned racquets-industry leader with 15+ years of experience across private clubs and commercial operations. Former Director of Racquets at Genesis Health Clubs.',
      image: '/shanky.png',
      linkedin: 'https://www.linkedin.com/in/shashank-nautiyal-7087aa211',
      email: 'shashankhill@gmail.com',
    },
    {
      id: 'robert-driscoll',
      name: 'Robert Driscoll',
      role: 'Chief Operating Officer',
      bio: 'Former nationally ranked squash player and finance executive managing over $600M in assets.',
      image: '/rob.png',
      linkedin: 'https://www.linkedin.com/in/rob-driscoll-81b208206',
      email: 'rob.driscoll9@gmail.com',
    },
    {
      id: 'freddie-mitchell',
      name: 'Freddie Mitchell',
      role: 'Head of Talent Acquisition',
      bio: 'Former NFL first-round draft pick and Philadelphia Eagles legend. Author of the iconic "4th and 26" play. Now dedicated to sports psychology, mental health advocacy, and mentoring athletes.',
      image: '/freddie.png',
      linkedin: 'http://linkedin.com/in/freddie-mitchell-b4131a125',
      email: 'fmitchell8406@gmail.com',
    },
    {
      id: 'tomas-kostik',
      name: 'Tomas Kostik',
      role: 'Partner',
      bio: 'Former top-10 nationally ranked junior in Germany with over two decades as a high-performance player, coach, and consultant, integrating analytics with elite development.',
      image: '/default.png',
      linkedin: 'https://www.linkedin.com/',
      email: 'tk24700@gmail.com',
    },
    {
      id: 'saif-ali',
      name: 'Saif Ali',
      role: 'Partner',
      bio: 'Global tennis consultant with 20+ years of international experience across four continents. Former touring coach for ATP professionals including former world No. 1 Rohan Bopanna.',
      image: '/saif.png',
      linkedin: 'https://www.linkedin.com/in/saif-ali-485b75198',
      email: 'saifalitennis@gmail.com',
    },
  ];

  const values = [
    {
      icon: <Target size={32} />,
      title: 'Excellence',
      description: 'We pursue the highest standards in everything we do, from club consulting to coaching methodologies.',
    },
    {
      icon: <Eye size={32} />,
      title: 'Innovation',
      description: 'We embrace cutting-edge technology and forward-thinking strategies to stay ahead of industry trends.',
    },
    {
      icon: <Heart size={32} />,
      title: 'Passion',
      description: 'Our love for racquet sports helps us create exceptional results for coaches, players, clubs, and communities.',
    },
    {
      icon: <Shield size={32} />,
      title: 'Integrity',
      description: 'We build lasting relationships through transparency, honesty, and unwavering ethical standards.',
    },
  ];

  return (
    <div className="about">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="about-hero-content">
            <h1 className="about-hero-title">About Baseline</h1>
            <p className="about-hero-subtitle">
              Pioneering the future of racquet sports through innovation, expertise, and passion
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section mission-section">
        <div className="container">
          <div className="mission-grid">
            <div className="mission-content">
              <h2 className="section-title">Our Mission</h2>
              <p className="mission-text">
                Baseline's mission is to provide professionals and clubs with the leadership knowledge and expertise needed to succeed in the hospitality and racquets industries
              </p>
            </div>
            
            <div className="mission-image">
              <img
                src="/mission.jpg"
                alt="Tennis court aerial view"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Difference Section */}
      <section className="section difference-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title text-center">The Baseline Difference</h2>
            <p className="section-subtitle text-center">
              What sets us apart in the world of racquet sports consulting
            </p>
          </div>

          <div className="difference-grid">
            <div className="difference-item">
              <h3 className="difference-title">Global Expertise</h3>
              <p className="difference-text">
                The Baseline team consists of professionals from across the globe, offering diverse perspectives and proven strategies that work in any market.
              </p>
            </div>

            <div className="difference-item">
              <h3 className="difference-title">Data-Driven Approach</h3>
              <p className="difference-text">
                We leverage advanced analytics and industry benchmarks to make informed decisions 
                that deliver measurable results for our members.
              </p>
            </div>

            <div className="difference-item">
              <h3 className="difference-title">End-to-End Solutions</h3>
              <p className="difference-text">
                From initial consultation to ongoing support, we provide comprehensive services 
                that address every aspect of racquet sports business and coaching.
              </p>
            </div>

            <div className="difference-item">
              <h3 className="difference-title">Proven Track Record</h3>
              <p className="difference-text">
                Our team has successfully transformed programs across a wide range of racquet institutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section values-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title text-center">Our Core Values</h2>
            <p className="section-subtitle text-center">
              The principles that guide everything we do
            </p>
          </div>

          <div className="values-grid">
            {values.map((value) => (
              <div key={value.title}>
                <Card variant="elevated" hoverable>
                  <div className="value-card">
                    <div className="value-icon">{value.icon}</div>
                    <h3 className="value-title">{value.title}</h3>
                    <p className="value-description">{value.description}</p>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section team-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title text-center">Meet Our Team</h2>
            <p className="section-subtitle text-center">
              Industry leaders with a shared passion for excellence
            </p>
          </div>

          <div className="team-grid">
            {team.map((member) => (
              <div key={member.name}>
                <Link to={`/team/${member.id}`} className="team-card-link">
                  <Card variant="bordered" hoverable>
                    <div className="team-card">
                      <div className="team-image-wrapper">
                        <img
                          src={member.image}
                          alt={member.name}
                          className="team-image"
                        />
                      </div>
                      <h3 className="team-name">{member.name}</h3>
                      <p className="team-role">{member.role}</p>
                      <p className="team-bio">{member.bio}</p>
                      <div className="team-social">
                        <a 
                          href={member.linkedin} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          aria-label={`${member.name} LinkedIn`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Linkedin size={20} />
                        </a>
                        <a 
                          href={`mailto:${member.email}`}
                          aria-label={`Email ${member.name}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Mail size={20} />
                        </a>
                      </div>
                    </div>
                  </Card>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section about-cta-section">
        <div className="container">
          <div className="about-cta-content">
            <h2 className="cta-title">Ready to Work With Us?</h2>
            <p className="cta-subtitle">
              Let's discuss how we can help transform your racquet sports business
            </p>
            <Button variant="primary" size="large" href="/contact">
              Get in Touch
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
